import {
  faCircleExclamation,
  faUpload,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Chip, Link, Stack, Tooltip } from "@mui/joy";
import { useQuery } from "@tanstack/react-query";
import { Fragment, memo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDatalabsBaseUrl } from "features/dataLabs/hooks";
import { type DataLab } from "features/dataLabs/models";
import {
  type DataLabCompatibilityResult,
  useMediaDataRoom,
  useMediaDataRoomHandlers,
} from "features/mediaDataRoom/contexts";

interface DataLabConnectionTableRowActionsProps {
  dataLab: DataLab;
  isConnecting: boolean;
  connectDataLab: (dataLab: DataLab) => Promise<void>;
  dataLabId: string;
}

const DataLabConnectionTableRowActions =
  memo<DataLabConnectionTableRowActionsProps>(
    ({ dataLab, connectDataLab, isConnecting, dataLabId }) => {
      const { dataRoomId } = useMediaDataRoom();
      const { canProvisionDataLab } = useMediaDataRoomHandlers();
      const { data: dataLabCompatibility } =
        useQuery<DataLabCompatibilityResult>({
          enabled: !!dataLab,
          queryFn: () => canProvisionDataLab(dataLab),
          queryKey: ["data-lab-compatibility", dataRoomId, dataLab?.id],
        });
      const dataLabBaseUrl = useDatalabsBaseUrl();
      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          {/* TODO: Handle case of initial load to do not show Incompatible by default */}
          {dataLab.isValidated ? (
            dataLabCompatibility?.isCompatible ? (
              <Button
                color="primary"
                disabled={isConnecting && dataLabId !== dataLab.id}
                loading={isConnecting && dataLabId === dataLab.id}
                loadingPosition="end"
                onClick={() => connectDataLab(dataLab)}
                size="sm"
                startDecorator={
                  <FontAwesomeIcon fixedWidth={true} icon={faUpload} />
                }
                variant="solid"
              >
                Provision datalab
              </Button>
            ) : (
              <Tooltip
                title={
                  <Stack spacing={0}>
                    This datalab is incompatible for at least one of the
                    following reasons:
                    <ul style={{ margin: 0 }}>
                      <li>
                        The matching ID in the datalab does not match the Media
                        DCR’s
                      </li>
                      <li>
                        Not all datasets required by the Media DCR are present
                        in the datalab
                      </li>
                      <li>
                        The Media DCR version is too old to work with this
                        datalab (please create a new Media DCR)
                      </li>
                    </ul>
                  </Stack>
                }
              >
                <Chip
                  color="warning"
                  startDecorator={
                    <FontAwesomeIcon icon={faCircleExclamation} />
                  }
                  variant="solid"
                >
                  Incompatible
                </Chip>
              </Tooltip>
            )
          ) : (
            <Tooltip
              title={
                <Fragment>
                  Open the{" "}
                  <Link
                    color="neutral"
                    component={RouterLink}
                    to={`${dataLabBaseUrl}/${dataLab.id}`}
                  >
                    datalab
                  </Link>{" "}
                  to validate it.
                </Fragment>
              }
            >
              <Chip
                color="warning"
                startDecorator={<FontAwesomeIcon icon={faCircleExclamation} />}
                variant="solid"
              >
                Not validated
              </Chip>
            </Tooltip>
          )}
        </Box>
      );
    }
  );

DataLabConnectionTableRowActions.displayName =
  "DataLabConnectionTableRowActions";

export default DataLabConnectionTableRowActions;
